import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSites } from '../../contexts/SitesContext';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import Tab from './Tab';
import Modal, { ModalCloseButton } from '../../shared/modals/Modal';
import './Panel.css';

import { ReactComponent as DashboardIcon } from '../../assets/icons/dashboard.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as Logo } from '../../assets/icons/logo.svg';
import { ReactComponent as Favicon } from '../../assets/icons/polygraph_favicon.svg';
import { ReactComponent as HelpIcon } from '../../assets/icons/help.svg';
import { ReactComponent as ProfileIcon } from '../../assets/icons/user.svg';
import { ReactComponent as SettingsIcon } from '../../assets/icons/settings.svg';
import { ReactComponent as LogoutIcon } from '../../assets/icons/logout.svg';
import { ReactComponent as MoreIcon } from '../../assets/icons/more.svg';

const FOOTER_ROW_BREAKPOINT = 620;

const Panel = ({ closeNav, setAddSiteModalVisible, isCompact = false, isMobile = false, isNavVisible = false }) => {
  const navigate = useNavigate();
  const { getSites, reorderSites } = useSites();
  const closeNavIfMobile = () => {
    if (isMobile) closeNav();
  };

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    reorderSites(source, destination);
  };

  const getPage = () => {
    const parts = window.location.pathname.split('/');
    return parts[parts.length - 1];
  };

  const currentPage = getPage();
  const labelProps = (label, { disableOnCompact = false } = {}) =>
    (isCompact && !disableOnCompact ? { 'data-collapsed-label': label } : {});
  const [websitesOverlayOpen, setWebsitesOverlayOpen] = useState(false);
  const [isWebsiteListOverflowing, setIsWebsiteListOverflowing] = useState(false);
  const [isFooterCompacted, setIsFooterCompacted] = useState(false);
  const [isLandscapeViewport, setIsLandscapeViewport] = useState(false);
  const [sidebarPreview, setSidebarPreview] = useState(null);
  const [footerMenuOpen, setFooterMenuOpen] = useState(false);
  const containerRef = useRef(null);
  const siteNavBodyRef = useRef(null);
  const footerMenuRef = useRef(null);
  const sites = useMemo(() => getSites(), [getSites]);
  const shouldUseCollapsedPreview = isCompact && !isMobile;
  const isLandscapeMobile = isMobile && isLandscapeViewport;
  const shouldUseDesktopFooterRow = !isMobile && !isCompact && isFooterCompacted;
  const shouldUseMobileFooterRow = isLandscapeMobile && isFooterCompacted;
  const shouldUseFooterMenu = !isMobile && isCompact && isFooterCompacted;
  const footerClassName = `app-nav__footer${shouldUseDesktopFooterRow ? ' app-nav__footer--row' : ''}${shouldUseMobileFooterRow ? ' app-nav__footer--mobile-row' : ''}${shouldUseFooterMenu ? ' app-nav__footer--compact-menu' : ''}`;
  const appNavLabelClassName = 'tab-label nav-item__text app-nav__label';
  const addWebsiteLabel = isLandscapeMobile ? 'Add website' : 'Add';
  const navLabelProps = (label, options = {}) => {
    const { forceCollapsedHint = false, ...rest } = options;
    if (forceCollapsedHint) return { 'data-collapsed-label': label };
    return labelProps(label, rest);
  };

  useEffect(() => {
    const updateLayout = () => {
      setIsLandscapeViewport(window.innerWidth > window.innerHeight);
      if (containerRef.current) {
        const nextHeight = containerRef.current.getBoundingClientRect().height;
        setIsFooterCompacted(nextHeight <= FOOTER_ROW_BREAKPOINT);
      }
      if (siteNavBodyRef.current) {
        const { scrollHeight, clientHeight } = siteNavBodyRef.current;
        setIsWebsiteListOverflowing(scrollHeight - clientHeight > 1);
      }
    };

    updateLayout();
    const raf = requestAnimationFrame(updateLayout);
    window.addEventListener('resize', updateLayout);
    window.addEventListener('orientationchange', updateLayout);
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(updateLayout);
      if (containerRef.current) observer.observe(containerRef.current);
      if (siteNavBodyRef.current) observer.observe(siteNavBodyRef.current);
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateLayout);
      window.removeEventListener('orientationchange', updateLayout);
      if (observer) observer.disconnect();
    };
  }, [sites.length]);

  useEffect(() => {
    if (!isNavVisible) {
      setWebsitesOverlayOpen(false);
      setSidebarPreview(null);
      setFooterMenuOpen(false);
    }
  }, [isNavVisible]);

  useEffect(() => {
    if (!footerMenuOpen) return undefined;
    const handlePointerDown = (event) => {
      if (footerMenuRef.current && !footerMenuRef.current.contains(event.target)) {
        setFooterMenuOpen(false);
      }
    };
    window.addEventListener('mousedown', handlePointerDown);
    return () => {
      window.removeEventListener('mousedown', handlePointerDown);
    };
  }, [footerMenuOpen]);

  useEffect(() => {
    if (!shouldUseFooterMenu) {
      setFooterMenuOpen(false);
    }
  }, [shouldUseFooterMenu]);

  const clearSidebarPreview = () => {
    setSidebarPreview(null);
  };

  const showSidebarPreview = (label, event, isSelected = false) => {
    if (!label) return;
    const target = event?.currentTarget;
    if (!target || typeof target.getBoundingClientRect !== 'function') return;
    const rect = target.getBoundingClientRect();
    setSidebarPreview({
      label,
      top: rect.top + (rect.height / 2),
      left: rect.right + 10,
      isSelected,
      placement: 'side'
    });
  };

  const previewProps = (label, isSelected = false, enabled = true, placement = 'side') => (
    enabled && label
      ? {
          onMouseEnter: (event) => {
            if (!label) return;
            const target = event?.currentTarget;
            if (!target || typeof target.getBoundingClientRect !== 'function') return;
            const rect = target.getBoundingClientRect();
            setSidebarPreview({
              label,
              isSelected,
              placement,
              top: placement === 'above-right' ? rect.top - 6 : rect.top + (rect.height / 2),
              left: placement === 'above-right' ? rect.right + 4 : rect.right + 10
            });
          },
          onMouseLeave: clearSidebarPreview
        }
      : {}
  );

  useEffect(() => {
    if (!websitesOverlayOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setWebsitesOverlayOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [websitesOverlayOpen]);

  const handleWebsiteOverlaySelect = () => {
    setWebsitesOverlayOpen(false);
    closeNavIfMobile();
  };

  const footerItems = [
    {
      key: 'profile',
      label: 'Profile',
      Icon: ProfileIcon,
      route: '/dashboard/profile'
    },
    {
      key: 'settings',
      label: 'Settings',
      Icon: SettingsIcon,
      route: '/dashboard/settings'
    },
    {
      key: 'help',
      label: 'Help',
      Icon: HelpIcon,
      route: '/dashboard/help'
    }
  ];

  const footerMenuItems = [
    ...footerItems,
    {
      key: 'logout',
      label: 'Log Out',
      Icon: LogoutIcon,
      action: () => {
        localStorage.removeItem('token');
        navigate('/');
      }
    }
  ];

  const renderFooterItem = ({ key, label, Icon, route }) => (
    <div
      key={key}
      className={currentPage === key ? 'tab app-nav__item selected-box' : 'tab app-nav__item unselected-box'}
      {...navLabelProps(label)}
      title={isCompact ? label : undefined}
      {...previewProps(label, currentPage === key, shouldUseDesktopFooterRow || shouldUseCollapsedPreview, shouldUseCollapsedPreview ? 'side' : 'above-right')}
      onClick={() => {
        closeNavIfMobile();
        navigate(route);
      }}
    >
      <Icon className="icon sm" />
      <span className={appNavLabelClassName}>
        {label}
      </span>
    </div>
  );

  const renderLogoutItem = () => (
    <button
      key="logout"
      type="button"
      className="tab app-nav__item app-nav__action unselected-box"
      {...navLabelProps('Log Out')}
      title={isCompact ? 'Log Out' : undefined}
      {...previewProps('Log Out', false, shouldUseDesktopFooterRow || shouldUseCollapsedPreview, shouldUseCollapsedPreview ? 'side' : 'above-right')}
      onClick={() => {
        closeNavIfMobile();
        localStorage.removeItem('token');
        navigate('/');
      }}
    >
      <LogoutIcon className="icon sm" />
      <span className={appNavLabelClassName}>
        Log Out
      </span>
    </button>
  );

  const renderSidebarWebsiteList = () => (
    <section className="site-nav" aria-label="Websites">
      {!isLandscapeMobile && (
        <div className="site-nav__header">
          <span className="tab-label site-nav__title">
            Websites
          </span>
          <button
            type="button"
            className="site-nav__add btn btn-action"
            {...labelProps('Add website')}
            title={isCompact ? 'Add website' : undefined}
            {...previewProps('Add website', false, shouldUseCollapsedPreview)}
            onClick={() => {
              closeNavIfMobile();
              setAddSiteModalVisible(true);
            }}
          >
            <PlusIcon className="site-nav__add-icon icon sm" />
            <span className="nav-item__text site-nav__add-label">{addWebsiteLabel}</span>
          </button>
        </div>
      )}

      {isLandscapeMobile && (
        <div className={`site-nav__controls${isWebsiteListOverflowing ? ' site-nav__controls--split' : ''}`}>
          <button
            type="button"
            className="site-nav__add btn btn-action"
            onClick={() => {
              closeNavIfMobile();
              setAddSiteModalVisible(true);
            }}
          >
            <PlusIcon className="site-nav__add-icon icon sm" />
            <span className="nav-item__text site-nav__add-label">{addWebsiteLabel}</span>
          </button>

          {isWebsiteListOverflowing && (
            <button
              type="button"
              className="site-nav__view-all btn btn-subtle"
              onClick={() => setWebsitesOverlayOpen(true)}
            >
              <span className="nav-item__text site-nav__view-all-label">View all websites</span>
              <span className="site-nav__view-all-count">{sites.length}</span>
              <span className="site-nav__view-all-compact">All</span>
            </button>
          )}
        </div>
      )}

      <div className="site-nav__surface">
        <div className="site-nav__body-viewport">
          <div className="site-nav__body" ref={siteNavBodyRef}>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="droppable" direction="vertical">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="site-nav__list">
                    {sites.map((site, index) => (
                      <Draggable key={site.website}
                                 draggableId={site.website}
                                 index={index}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}>
                            <Tab site={site}
                                 onSelect={closeNavIfMobile}
                                 dragHandleProps={provided.dragHandleProps}
                                 hideDrag={isMobile}
                                 compact={isCompact}
                                 previewEnabled={shouldUseCollapsedPreview}
                                 onPreviewChange={showSidebarPreview}
                                 onPreviewClear={clearSidebarPreview}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </div>
      </div>

      {isWebsiteListOverflowing && !isLandscapeMobile && (
        <div className="site-nav__footer-action">
          <button
            type="button"
            className="site-nav__view-all btn btn-subtle"
            data-collapsed-label={`View all ${sites.length} websites`}
            title={isCompact ? `View all ${sites.length} websites` : undefined}
            {...previewProps(`View all ${sites.length} websites`, false, shouldUseCollapsedPreview)}
            onClick={() => setWebsitesOverlayOpen(true)}
          >
            <span className="nav-item__text site-nav__view-all-label">View all websites</span>
            <span className="site-nav__view-all-count">{sites.length}</span>
            <span className="site-nav__view-all-compact">All</span>
          </button>
        </div>
      )}
    </section>
  );

  const renderWebsiteModalList = () => (
    <Modal
      isOpen={websitesOverlayOpen}
      onClose={() => setWebsitesOverlayOpen(false)}
      className="modal--medium modal--short"
    >
      <div className="modal">
        <div className="modal__header">
          <div className="modal__title-inline">
            <h3 className="margin-none">All websites</h3>
            <span className="modal__badge">{sites.length}</span>
          </div>
          <ModalCloseButton
            onClick={() => setWebsitesOverlayOpen(false)}
            label="Close websites list"
          />
        </div>

        <div className="modal__body">
          <button
            type="button"
            className="site-nav-modal__add btn btn-subtle"
            onClick={() => {
              setWebsitesOverlayOpen(false);
              closeNavIfMobile();
              setAddSiteModalVisible(true);
            }}
          >
            <PlusIcon className="icon sm" />
            <span>Add website</span>
          </button>

          <div className="modal-list site-nav-list" role="list">
            {sites.map((site) => (
              <Tab
                key={site.website}
                site={site}
                onSelect={handleWebsiteOverlaySelect}
                hideDrag={true}
                compact={false}
                disableLabelHint={true}
              />
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );

  return (
    <div className={`panel-shell${isCompact ? ' is-compact' : ''}`} ref={containerRef}>
      <div className="panel-brand">
        {isCompact ? (
          <Favicon className="panel-brand__image" onClick={() => navigate('/')} />
        ) : (
          <Logo className="panel-brand__image" onClick={() => navigate('/')} />
        )}
      </div>

      <div className="app-nav">
        <div className="app-nav__primary">
          <div
            className={currentPage === 'dashboard' ? 'tab app-nav__item selected-box' : 'tab app-nav__item unselected-box'}
            {...labelProps('Dashboard')}
            title={isCompact ? 'Dashboard' : undefined}
            {...previewProps('Dashboard', currentPage === 'dashboard', shouldUseCollapsedPreview)}
            onClick={() => {
              closeNavIfMobile();
              navigate('/dashboard');
            }}>
            <DashboardIcon className="icon sm" />
            <span className={appNavLabelClassName}>
              Dashboard
            </span>
          </div>

          {renderSidebarWebsiteList()}
        </div>

      <div className={footerClassName}>
          {shouldUseFooterMenu ? (
            <div className="app-nav__footer-compact" ref={footerMenuRef}>
              <button
                type="button"
                className={`tab app-nav__item app-nav__overflow-trigger${footerMenuOpen ? ' selected-box' : ' unselected-box'}`}
                title="More"
                aria-expanded={footerMenuOpen}
                aria-haspopup="menu"
                {...previewProps('More', false, true)}
                onClick={() => setFooterMenuOpen((value) => !value)}
              >
                <MoreIcon className="icon sm" />
              </button>

              {footerMenuOpen && (
                <div className="app-nav__overflow-popover" role="menu" aria-label="Footer navigation">
                  {footerMenuItems.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      className={currentPage === item.key
                        ? 'app-nav__overflow-item selected-box'
                        : 'app-nav__overflow-item unselected-box'}
                      onClick={() => {
                        setFooterMenuOpen(false);
                        closeNavIfMobile();
                        if (item.route) navigate(item.route);
                        if (item.action) item.action();
                      }}
                    >
                      <item.Icon className="icon sm" />
                      <span className="nav-item__text">
                        {item.label}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="app-nav__footer-group">
                {footerItems.map(renderFooterItem)}
              </div>
              <div className="app-nav__footer-action">
                {renderLogoutItem()}
              </div>
            </>
          )}
        </div>
      </div>

      {sidebarPreview && (
        <div className="sidebar-preview-layer" aria-hidden="true">
          <div
            className={`sidebar-preview sidebar-preview--${sidebarPreview.placement || 'side'} visible${sidebarPreview.isSelected ? ' selected' : ''}`}
            style={{ top: sidebarPreview.top, left: sidebarPreview.left }}
          >
            <div className={`sidebar-preview__card${sidebarPreview.isSelected ? ' selected-box' : ' unselected-box'}`}>
              <span className="nav-item__text">{sidebarPreview.label}</span>
            </div>
          </div>
        </div>
      )}

      {renderWebsiteModalList()}
    </div>
  );
};

export default Panel;
